import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input, Button, Table, Dropdown } from "antd";
import type { MenuProps, TableColumnsType } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { ApolloError } from "@apollo/client";
import { HiSearch } from "react-icons/hi";
import dayjs from "dayjs";

import { Users, Organizations, Properties } from "graphql/generated/graphql";
import { useGetUsersFilteredQuery } from "graphql/queries/get-users-filtered.generated";
import { useGetUsersByOrganizationIdsQuery } from "graphql/queries/get-users-by-organization-ids.generated";
import { useGetUserInvitationsByOrganizationIdQuery } from "graphql/queries/get-user-invitations-by-organization-id.generated";

import routeNames from "constants/route-names";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAdminUsersFiltersStore } from "state/zustand-stores/admin-users-filters-store";
import { UserRoleLabel } from "constants/field-values";
import { createLabelForFilters } from "utils/helpers";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";

const tableColumns: TableColumnsType = [
  {
    title: "FIRST NAME",
    dataIndex: "first_name",
    key: "FIRST NAME",
  },
  {
    title: "LAST NAME",
    dataIndex: "last_name",
    key: `LAST NAME`,
  },
  {
    title: "ORGANIZATION",
    render: (field, record, index) => {
      if (record.organization && record.organization.name) {
        return `${record.organization.name}`;
      }

      if (record.user_properties && record.user_properties.length) {
        const firstProp = record.user_properties[0];
        const { property } = firstProp || {};

        if (property && property.organization) {
          return `${property.organization.name}`;
        }
      }

      return ``;
    },
    key: `ORGANIZATION`,
  },
  {
    title: "ROLE",
    render: (field, record, index) => {
      if (record.role) {
        return `${UserRoleLabel[record.role]}`;
      }

      return ``;
    },
    key: `ROLE`,
  },
  {
    title: "TITLE",
    dataIndex: "title",
    key: `TITLE`,
  },
];

const invitationsTableColumns: TableColumnsType = [
  {
    title: "EMAIL",
    dataIndex: "email",
    key: "EMAIL",
  },
  {
    title: "STATUS",
    dataIndex: "status",
    key: `STATUS`,
  },
  {
    title: "INVITATION DATE",
    dataIndex: "created_at",
    render: (item) => {
      const formattedDate = dayjs(item).format("MM/DD/YYYY HH:mm:ss");
      return formattedDate;
    },
    key: `INVITATION`,
  },
];

const AdminUsers: React.FC = () => {
  const { loggedUserValue } = useLoggedUser();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<Array<Users>>([]);
  const [propertyOptions, setPropertyOptions] = useState<Array<Properties>>([]);
  const [organizationOptions, setOrganizationOptions] = useState<
    Array<Organizations>
  >([]);
  const [propertiesDropdownOpen, setPropertiesDropdownOpen] =
    useState<boolean>(false);
  const [organizationsDropdownOpen, setOrganizationsDropdownOpen] =
    useState<boolean>(false);

  const selectedPropertiesFilter = useAdminUsersFiltersStore(
    (state) => state.filters.selectedPropertiesFilter
  );

  const setPropertyFilters = useAdminUsersFiltersStore(
    (state) => state.setPropertyFilters
  );

  const selectedOrganizationsFilter = useAdminUsersFiltersStore(
    (state) => state.filters.selectedOrganizationsFilter
  );

  const setOrganizationFilters = useAdminUsersFiltersStore(
    (state) => state.setOrganizationFilters
  );

  const searchTerm = useAdminUsersFiltersStore(
    (state) => state.filters.searchAdminUsersTerm
  );

  const setSearchTerm = useAdminUsersFiltersStore(
    (state) => state.setSearchTerm
  );

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    SEARCH_DEBOUNCE_DELAY_MS
  );

  useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [searchTerm]);

  const usersFilter = useMemo(() => {
    const whereClause: any = {
      workspace_id: { _eq: loggedUserValue.workspace_id },
      organization_id: { _eq: loggedUserValue.organization_id },
      deleted_at: { _is_null: true },
    };

    if (
      (selectedPropertiesFilter && selectedPropertiesFilter.length) ||
      (selectedOrganizationsFilter && selectedOrganizationsFilter.length)
    ) {
      if (
        selectedPropertiesFilter.length &&
        selectedOrganizationsFilter.length
      ) {
        whereClause.user_properties = {
          property_id: { _in: selectedPropertiesFilter },
          property: {
            organization: { id: { _in: selectedOrganizationsFilter } },
          },
        };
      }

      if (
        selectedPropertiesFilter.length &&
        selectedOrganizationsFilter.length === 0
      ) {
        whereClause.user_properties = {
          property_id: { _in: selectedPropertiesFilter },
        };
      }

      if (
        selectedPropertiesFilter.length === 0 &&
        selectedOrganizationsFilter.length
      ) {
        whereClause.user_properties = {
          property: {
            organization: { id: { _in: selectedOrganizationsFilter } },
          },
        };
      }
    } else if (whereClause.user_properties) {
      delete whereClause.user_properties;
    }

    // TODO: Relate users with organizations
    // if (selectedOrganizationsFilter && selectedOrganizationsFilter.length) {
    //   whereClause.organization = {
    //     id: { _in: selectedOrganizationsFilter },
    //   };

    //   whereClause.user_properties = {
    //     property: {
    //       organization: { id: { _in: selectedOrganizationsFilter } },
    //     },
    //   };
    // } else if (whereClause.organization) {
    //   delete whereClause.organization;
    // }

    if (debouncedSearchTerm) {
      whereClause._or = [
        { first_name: { _ilike: `%${debouncedSearchTerm}%` } },
        { last_name: { _ilike: `%${debouncedSearchTerm}%` } },
        { email: { _ilike: `%${debouncedSearchTerm}%` } },
        { role: { _ilike: `%${debouncedSearchTerm}%` } },
        { title: { _ilike: `%${debouncedSearchTerm}%` } },
      ];
    }

    return whereClause;
  }, [
    debouncedSearchTerm,
    loggedUserValue,
    selectedOrganizationsFilter,
    selectedPropertiesFilter,
  ]);

  const handleOnClickUser = useCallback((record) => {
    const { id } = record;
    const path = `${routeNames.ADMIN_USER_DETAILS.replace(":userId", id)}`;

    navigate(path);
  }, []);

  const handleOnCompleteUsers = useCallback((data) => {
    const { users } = data;

    setDataSource(users);
  }, []);

  const handleOnErrorUsers = useCallback((error: ApolloError) => {
    console.log("handleOnErrorUsers Error: ", error);
  }, []);

  const { loading } = useGetUsersFilteredQuery({
    variables: {
      where: usersFilter,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorUsers,
    onCompleted: handleOnCompleteUsers,
  });

  const handleOnCompleteUsersByOrganization = useCallback((data) => {
    const { users } = data;

    const propertiesSet: Set<string> = new Set();
    const organizationsSet: Set<string> = new Set();

    users.forEach((user) => {
      const { user_properties, organization } = user;

      if (organization) {
        const organizationString = JSON.stringify(organization);
        organizationsSet.add(organizationString);
      }

      user_properties.forEach((userprop) => {
        const { property } = userprop || {};
        if (property) {
          const propertyString = JSON.stringify(property);
          propertiesSet.add(propertyString);

          if (property.organization) {
            const organizationString = JSON.stringify(property.organization);
            organizationsSet.add(organizationString);
          }
        }
      });
    });

    const properties: Array<Properties> = [...propertiesSet].map((element) =>
      JSON.parse(element)
    );
    setPropertyOptions(properties);

    const organizations: Array<Organizations> = [...organizationsSet].map(
      (element) => JSON.parse(element)
    );
    setOrganizationOptions(organizations);
  }, []);

  const handleOnErrorUsersByOrganization = useCallback((error: ApolloError) => {
    console.log("handleOnErrorUsers Error: ", error);
  }, []);

  const { loading: loadingUsersByOrganization } =
    useGetUsersByOrganizationIdsQuery({
      variables: {
        organization_ids: [loggedUserValue?.organization_id],
      },
      errorPolicy: "none",
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorUsersByOrganization,
      onCompleted: handleOnCompleteUsersByOrganization,
    });

  const handleOnCompleteGetUserInvitations = useCallback((data) => {
    //console.log("GetUserInvitations complete data: ", data);
  }, []);

  const handleOnErrorGetUserInvitations = useCallback((error: ApolloError) => {
    console.log("GetUserInvitations Error: ", error);
  }, []);

  const { data: userInvitationsData, loading: loadingGetUserInvitations } =
    useGetUserInvitationsByOrganizationIdQuery({
      skip: !loggedUserValue?.organization_id,
      variables: {
        organization_id: loggedUserValue?.organization_id,
      },
      errorPolicy: "none",
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorGetUserInvitations,
      onCompleted: handleOnCompleteGetUserInvitations,
    });

  const handleSearchTermChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Directly update the search term in Zustand
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const handleOnSelectPropertiesFilter = useCallback(
    (args) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("property-dropdown-option-all")) {
        setPropertyFilters([]);
        return;
      }

      setPropertyFilters(selectedKeys);
    },
    [setPropertyFilters]
  );

  const handleOnSelectOrganizationsFilter = useCallback(
    (args) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("organization-dropdown-option-all")) {
        setOrganizationFilters([]);
        return;
      }

      setOrganizationFilters(selectedKeys);
    },
    [setOrganizationFilters]
  );

  const [propertiesOptions, propertiesDropdownLabel = "Property"] =
    useMemo(() => {
      if (propertyOptions && propertyOptions.length) {
        let selectedPropertyName;

        const productProperties: MenuProps["items"] = propertyOptions.reduce(
          (options, property, currentindex, array) => {
            const optionId = `property-dropdown-option-${currentindex + 1}-${
              property?.id
            }`;

            const isSelected = selectedPropertiesFilter.includes(
              property?.id as never
            );

            if (!selectedPropertyName && isSelected) {
              selectedPropertyName = property.name;
            }

            const newOption = {
              key: `${property?.id}`,
              icon: (
                <div>
                  <input
                    className="custom-checkbox"
                    id={optionId}
                    type="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    checked={isSelected}
                    onChange={() => undefined}
                  />
                  <label htmlFor={optionId} className="custom-checkbox-label">
                    {property?.name}
                  </label>
                </div>
              ),
            };

            options.push(newOption);

            return options;
          },
          [
            {
              key: "property-dropdown-option-all",
              icon: (
                <div>
                  <input
                    className="custom-checkbox"
                    id="property-dropdown-option-all"
                    type="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label
                    htmlFor="property-dropdown-option-all"
                    className="custom-checkbox-label"
                  >
                    All properties
                  </label>
                </div>
              ),
            },
          ]
        );

        return [
          productProperties,
          createLabelForFilters(
            "Property",
            selectedPropertiesFilter,
            selectedPropertyName
          ),
        ];
      }

      return [[], "Property"];
    }, [propertyOptions, selectedPropertiesFilter]);

  const [organizationsOptions, organizationsDropdownLabel = "Organization"] =
    useMemo(() => {
      if (organizationOptions && organizationOptions.length) {
        let selectedOrganizationName;

        const userOrganizations: MenuProps["items"] =
          organizationOptions.reduce(
            (options, organization, currentindex, array) => {
              const optionId = `organization-dropdown-option-${
                currentindex + 1
              }-${organization?.id}`;

              const isSelected = selectedOrganizationsFilter.includes(
                organization?.id as never
              );

              if (!selectedOrganizationName && isSelected) {
                selectedOrganizationName = organization.name;
              }

              const newOption = {
                key: `${organization?.id}`,
                icon: (
                  <div>
                    <input
                      className="custom-checkbox"
                      id={optionId}
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                      checked={isSelected}
                      onChange={() => undefined}
                    />
                    <label htmlFor={optionId} className="custom-checkbox-label">
                      {organization?.name}
                    </label>
                  </div>
                ),
              };

              options.push(newOption);

              return options;
            },
            [
              {
                key: "organization-dropdown-option-all",
                icon: (
                  <div>
                    <input
                      className="custom-checkbox"
                      id="organization-dropdown-option-all"
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <label
                      htmlFor="organization-dropdown-option-all"
                      className="custom-checkbox-label"
                    >
                      All organizations
                    </label>
                  </div>
                ),
              },
            ]
          );

        return [
          userOrganizations,
          createLabelForFilters(
            "Organization",
            selectedOrganizationsFilter,
            selectedOrganizationName
          ),
        ];
      }

      return [[], "Organization"];
    }, [organizationOptions, selectedOrganizationsFilter]);

  const isLoading =
    loadingUsersByOrganization || loading || loadingGetUserInvitations;

  return (
    <div className="products-screen-container">
      <span className="page-title">{"Manage Users"}</span>
      <div className="admin-products-content-container">
        <div className="admin-products-filter-group">
          <div className="search-products-row">
            <div className="products-search-container">
              <Input
                className="product-search-input-wrapper"
                name="searchUser"
                placeholder="Search..."
                classNames={{ input: "product-search-input" }}
                value={searchTerm} // Use Zustand search term
                autoComplete="off"
                onChange={handleSearchTermChange}
                prefix={<HiSearch color={"#67748E"} size={"16px"} />}
                allowClear
              />
            </div>
          </div>

          <div className="products-filters-and-create-guide-row">
            <div className="products-filters-row">
              <Dropdown
                menu={{
                  items: propertiesOptions,
                  selectable: true,
                  multiple: true,
                  onSelect: (args) => handleOnSelectPropertiesFilter(args),
                  onDeselect: (args) => handleOnSelectPropertiesFilter(args),
                  selectedKeys: selectedPropertiesFilter,
                }}
                arrow
                className="products-dropdown"
                overlayClassName="products-dropdown-overlay"
                trigger={["click"]}
                open={propertiesDropdownOpen}
                onOpenChange={(
                  open: boolean,
                  info: { source: "trigger" | "menu" }
                ) => {
                  if (info.source === "trigger" && open === false) {
                    setPropertiesDropdownOpen(false);
                  }
                }}
                disabled={propertiesOptions.length === 0}
              >
                <Button
                  onClick={() => {
                    setPropertiesDropdownOpen(
                      (previousValue) => !previousValue
                    );
                  }}
                >
                  {propertiesDropdownLabel}
                  <img
                    src={
                      process.env.PUBLIC_URL +
                      "/assets/images/icons/black-chevron-down.svg"
                    }
                    style={{ width: "1em", height: "1em" }}
                    title={propertiesDropdownLabel}
                    className="chevron-button"
                  />
                </Button>
              </Dropdown>
              <Dropdown
                menu={{
                  items: organizationsOptions,
                  selectable: true,
                  multiple: true,
                  onSelect: (args) => handleOnSelectOrganizationsFilter(args),
                  onDeselect: (args) => handleOnSelectOrganizationsFilter(args),
                  selectedKeys: selectedOrganizationsFilter,
                }}
                arrow
                className="products-dropdown"
                overlayClassName="products-dropdown-overlay"
                trigger={["click"]}
                open={organizationsDropdownOpen}
                onOpenChange={(
                  open: boolean,
                  info: { source: "trigger" | "menu" }
                ) => {
                  if (info.source === "trigger" && open === false) {
                    setOrganizationsDropdownOpen(false);
                  }
                }}
                disabled={organizationsOptions.length === 0}
              >
                <Button
                  onClick={() => {
                    setOrganizationsDropdownOpen(
                      (previousValue) => !previousValue
                    );
                  }}
                >
                  {organizationsDropdownLabel}
                  <img
                    src={
                      process.env.PUBLIC_URL +
                      "/assets/images/icons/black-chevron-down.svg"
                    }
                    title={organizationsDropdownLabel}
                    className="chevron-button"
                  />
                </Button>
              </Dropdown>
            </div>

            <Link to={routeNames.ADMIN_USER_ADD} className="highnote-button">
              Add User
            </Link>
          </div>
        </div>

        <div className="admin-user-tables-container">
          <div className="products-table-container">
            <Table
              dataSource={dataSource}
              columns={tableColumns}
              loading={isLoading}
              rowKey={(record) => record.id}
              onRow={(record, rowIndex) => {
                return {
                  onClick: (event) => {
                    handleOnClickUser(record);
                  },
                };
              }}
              style={{ marginBottom: "25px" }}
            />
          </div>

          <span className="page-title">{"User Invitations"}</span>
          <div className="products-table-container">
            <Table
              dataSource={userInvitationsData?.user_invitations}
              columns={invitationsTableColumns}
              loading={isLoading}
              rowKey={(record) => record.id}
              style={{ margin: "25px 0px" }}
              // onRow={(record, rowIndex) => {
              //   return {
              //     onClick: (event) => {
              //       handleOnClickUser(record);
              //     },
              //   };
              // }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
